import { useEffect, useRef, useCallback } from 'react'
import type { Listener } from './types'
import { addAnalyticListener, removeAnalyticListener } from './listener'
import { isEmpty, SECOND, Logger, type Analytic } from '@terros/common'
import { useAddAnalytics } from './add'

const logger = new Logger('AnalyticsPublisher')

export type UseAnalyticsPublisherConfig = {
  sessionId: string
  isDev: boolean
}

export function useAnalyticsPublisher(config: UseAnalyticsPublisherConfig): void {
  const timer = useRef<NodeJS.Timeout | null>(null)
  const analytics = useRef<Analytic[]>([])
  const { mutateAsync } = useAddAnalytics()

  const publish = useCallback(() => {
    if (isEmpty(analytics.current)) return
    const toPublish = analytics.current.slice()
    analytics.current = []
    if (config.isDev) {
      logger.verbose('Skipping analytics publish in DEV mode')
      return
    }
    logger.info(`Publishing ${toPublish.length} analytics`)
    mutateAsync({
      sessionId: config.sessionId,
      analytics: toPublish,
    }).catch((error) => {
      logger.error('Failed to publish analytics', error)
    })
  }, [mutateAsync, config.sessionId, config.isDev])

  const maybePublish = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current)
    if (analytics.current.length >= 20) {
      publish()
    } else {
      timer.current = setTimeout(publish, 10 * SECOND)
    }
  }, [publish])

  const onAnalytic = useCallback<Listener>(
    (analytic) => {
      analytics.current.push(analytic)
      maybePublish()
    },
    [maybePublish]
  )

  useEffect(() => {
    const listener = addAnalyticListener(onAnalytic)
    return () => {
      removeAnalyticListener(listener)
    }
  }, [onAnalytic])
}
